/**
 * EMAIL API — Wrapper REST allineato a EmailsResourceImpl.java.
 * I metodi `getById`/`create`/`update`/`remove` vengono da `createCrudApi`; solo
 * `getAll` è ridefinito perché la list ha `direction` nel PATH (non standard).
 *
 * Contratto backend (rest-openapi.yaml):
 *   - GET    /emails/{groupId}/{direction}?search=&page=&pageSize=  -> Page<Email>
 *   - POST   /emails/{groupId}  EmailInput                          -> Email
 *   - GET    /email/{emailId}                                         -> Email
 *   - PATCH  /email/{emailId}   EmailInput (parziale)                -> Email
 *   - DELETE /email/{emailId}                                         -> 200
 *
 * Come Call/Messages: `direction` nel PATH. Senza direction la rotta
 * non matcha — `getAll` la richiede. `emailUpdateToInput` ignora `emailId` (whitelist).
 */

import type { Page, ServerListParams } from '@crm/shared';
import { httpClient } from '../../api/httpClient';
import { createCrudApi } from '../../store/createCrudApi';
import {
  emailFromDto,
  emailCreateToInput,
  emailUpdateToInput,
  type EmailDto,
} from './emailMappers';
import type { Email, EmailCreatePayload, EmailDirection, EmailUpdatePayload } from './emailTypes';

const COLLECTION = '/emails';
const SINGLE = '/email';

/**
 * Parametri di list. `direction` formalmente opzionale per essere
 * compatibile con la firma `CrudApi.getAll(groupId, params?)` usata da
 * `createCrudThunks` / `registerEntityType`. Quando assente, il client
 * applica default 'OUT'.
 */
export interface EmailListParams extends ServerListParams {
  direction?: EmailDirection;
}

const base = createCrudApi<Email, EmailDto, EmailCreatePayload, EmailUpdatePayload, EmailListParams>({
  collection: COLLECTION,
  single: SINGLE,
  fromDto: emailFromDto,
  createToInput: emailCreateToInput,
  updateId: (p) => p.emailId,
  updateToInput: (p) => emailUpdateToInput(p),
});

export const emailApi = {
  ...base,

  /**
   * Override: la list ha `direction` come segmento di PATH
   * (`/emails/{groupId}/{direction}`), forma che la factory non copre. Default
   * 'OUT' quando omessa; gli altri filtri viaggiano come query.
   */
  getAll: (groupId: number, params?: EmailListParams): Promise<Page<Email>> => {
    const { direction = 'OUT', ...rest } = params ?? {};
    return httpClient
      .get<Page<EmailDto>>(`${COLLECTION}/${groupId}/${direction}`, {
        params: rest as Record<string, unknown>,
      })
      .then((r) => ({
        ...r.data,
        items: r.data.items.map(emailFromDto),
      }));
  },
};
